export interface MinutaDocumentModel {
  clientName: string;
  projectName: string;
  unitArea: string;
  osNumber: string;
  tag: string;
  equipmentType: string;
  projectCode?: string | undefined;
  fabricationCode?: string | undefined;
  fabricationStandard: string;
  /**
   * SPEC / especificação de padronização de material, além da norma de
   * fabricação (`fabricationStandard`) — os dois juntos compõem o campo fixo
   * "ESPECIFICAÇÃO TÉCNICA DE PADRONIZAÇÃO DE MATERIAL" do cabeçalho
   * (Melhoria 012, versão definitiva). Ausente/"N/A" quando o Equipamento
   * não tem SPEC cadastrada — nunca inventado.
   */
  materialSpec?: string | undefined;
  /**
   * SISTEMA de Tubulação do Equipamento (`Equipment.system`), para o campo
   * fixo "SISTEMA:" do cabeçalho. Vazio/"—" quando o Equipamento não tem
   * Sistema aplicável (mesma regra da Melhoria 002 — nunca um Sistema
   * fictício é inventado para preencher o campo).
   */
  systemName?: string | undefined;
  inspectorName: string;
  /** P0-02 V4 Causa 11: tipo de execução obrigatório no modelo documental.
   *  O Builder valida consistência entre este campo e as recomendações dos grupos. */
  executionType: 'PARADA' | 'ROTINA';
  trackingNumber?: string | undefined;
  revisionNumber?: string | undefined;
  printDate?: string | undefined;
  groups: MinutaGroup[];
  tenantLogo?: string | null | undefined;
  /**
   * Avaliação de Engenharia do RI de origem (Painel de Engenharia): condição
   * macro, resultado derivado e Vida Residual adotada em meses. Dados-fonte —
   * vêm do Relatório, nunca são redigitados na Minuta.
   */
  assessment?: MinutaAssessmentInfo | undefined;
  /**
   * PLH electronic approval/signature, frozen into THIS revision's snapshot
   * at the moment of approval (Bloco 7, Fase 29) — never recomputed from the
   * Professional's current record on a later print. Absent while the
   * revision is not yet approved (a Preview before approval must never show
   * a signature as if it already happened).
   */
  approval?: MinutaApprovalInfo | undefined;
}

export interface MinutaAssessmentInfo {
  /** "APTO" | "NÃO APTO" | "A CRITÉRIO DO CLIENTE" */
  equipmentCondition: string;
  /** "APROVADO" | "REPROVADO" | "N/A" */
  equipmentResult: string;
  residualLifeAdoptedMonths: number | null;
  residualLifeCalculatedMonths: number | null;
  /** "AVR" | "INFORMADA PELO ENGENHEIRO" */
  residualLifeOrigin: string;
  /** "AVR-2026-0001 / Rev. 00 / 18/09/2026" quando a origem é AVR. */
  residualLifeSource: string;
}

export interface MinutaApprovalInfo {
  /** data:image/... base64, or null when the Professional has no signature image registered. */
  signatureImage: string | null;
  plhName: string;
  functionTitle: string;
  /** e.g. "CREA: 12345 | CFT: 6789" — empty string when no registration field is present. */
  registration: string;
  /** ISO timestamp of the approval. */
  approvedAt: string;
  /** First characters of the full SHA-256 hash, for on-document display only — the complete hash stays internal (Minuta.signatureHash). */
  hashShort: string;
}

export interface MinutaGroup {
  discipline: string;
  recommendations: MinutaRecommendation[];
}

export interface MinutaRecommendation {
  id?: string | undefined;
  recommendationId?: string | undefined;
  // P0-02 V5.2 Falha 1: discipline é obrigatório para evitar undefined no snapshot/builder.
  discipline: string;
  line: string;
  title: string;
  body: string;
  zr: string;
  subItems?: string[] | undefined;
  fullDescription?: string | undefined;
  recommendationNumber?: string | undefined;
  componentName?: string | undefined;
  location?: string | undefined;
  executionType?: string | undefined;
  criticality?: string | undefined;
  scope?: string | undefined;
  dimensions?: string | undefined;
  standard?: string | undefined;
  procedure?: string | undefined;
  observation?: string | undefined;
  notes?: string | undefined;
}

export interface MinutaDiffItem {
  type: 'NEW' | 'MODIFIED' | 'REMOVED';
  id: string;
  discipline: string;
  componentName: string;
  firstLine: string;
  zr: string;
  origin: string;
  changes?: string[];
}

export interface MinutaDiffResult {
  hasChanges: boolean;
  totalCurrent: number;
  totalSnapshot: number;
  newCount: number;
  modifiedCount: number;
  removedCount: number;
  pendingCount: number;
  items: MinutaDiffItem[];
}

export function parseRecommendationText(description: string): { title: string; body: string } {
  if (!description) return { title: '', body: '' };

  const normalized = description.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = normalized.split('\n');

  let titleIndex = 0;
  while (titleIndex < lines.length && (lines[titleIndex] || '').trim() === '') {
    titleIndex++;
  }

  if (titleIndex >= lines.length) {
    return { title: '', body: '' };
  }

  const title = (lines[titleIndex] || '').trim();
  const body = lines.slice(titleIndex + 1).join('\n').trim();

  return { title, body };
}

export function compareRecommendationsWithMinutaSnapshot(
  checklistRecs: any[],
  snapshotData: any
): MinutaDiffResult {
  if (!snapshotData) {
    return {
      hasChanges: false,
      totalCurrent: checklistRecs.length,
      totalSnapshot: 0,
      newCount: 0,
      modifiedCount: 0,
      removedCount: 0,
      pendingCount: 0,
      items: []
    };
  }

  const model: MinutaDocumentModel = snapshotData.data || snapshotData;
  const snapshotRecs: MinutaRecommendation[] = [];
  if (model && Array.isArray(model.groups)) {
    for (const group of model.groups) {
      if (Array.isArray(group.recommendations)) {
        snapshotRecs.push(...group.recommendations);
      }
    }
  }

  const snapshotMap = new Map<string, MinutaRecommendation>();
  snapshotRecs.forEach(r => {
    const key = r.recommendationId || r.id;
    if (key) snapshotMap.set(key, r);
  });

  const checklistMap = new Map<string, any>();
  checklistRecs.forEach(r => {
    if (r.id) checklistMap.set(r.id, r);
  });

  const diffItems: MinutaDiffItem[] = [];
  let newCount = 0;
  let modifiedCount = 0;
  let removedCount = 0;

  // 1. Check for NEW and MODIFIED
  for (const chkRec of checklistRecs) {
    const snapRec = snapshotMap.get(chkRec.id);
    const desc = chkRec.description || chkRec.scope || '';
    const { title } = parseRecommendationText(desc);
    const firstLine = title || chkRec.componentName || 'Recomendação Técnica';
    const zrDisplay = chkRec.zr && chkRec.zr.trim() ? chkRec.zr.trim() : '—';

    if (!snapRec) {
      newCount++;
      diffItems.push({
        type: 'NEW',
        id: chkRec.id,
        // P0-02 V5.1: fallback 'OUTRA' para evitar agrupamento errôneo como Caldeiraria (1.x)
        discipline: chkRec.discipline || 'OUTRA',
        componentName: chkRec.componentName || chkRec.component || 'Componente',
        firstLine,
        zr: zrDisplay,
        origin: 'Checklist de Inspeção'
      });
    } else {
      const changes: string[] = [];
      const snapDesc = snapRec.fullDescription || snapRec.scope || '';
      if (desc.trim() !== snapDesc.trim()) changes.push('Descritivo / Escopo');
      if ((chkRec.zr || '').trim() !== (snapRec.zr || '').trim()) changes.push('ZR');
      if (chkRec.criticality && snapRec.criticality && (chkRec.criticality || '').toUpperCase() !== snapRec.criticality.toUpperCase()) {
        changes.push('Criticidade');
      }
      const chkDim = chkRec.dimensions || chkRec.dimension || '';
      const snapDim = snapRec.dimensions || '';
      if (chkDim && chkDim.trim() !== snapDim.trim()) {
        changes.push('Dimensão');
      }

      if (changes.length > 0) {
        modifiedCount++;
        diffItems.push({
          type: 'MODIFIED',
          id: chkRec.id,
          discipline: chkRec.discipline || 'CALDEIRARIA',
          componentName: chkRec.componentName || chkRec.component || 'Componente',
          firstLine,
          zr: zrDisplay,
          origin: 'Checklist de Inspeção',
          changes
        });
      }
    }
  }

  // 2. Check for REMOVED
  for (const snapRec of snapshotRecs) {
    const key = snapRec.recommendationId || snapRec.id;
    if (key && !checklistMap.has(key)) {
      removedCount++;
      diffItems.push({
        type: 'REMOVED',
        id: key,
        // P0-02 V5.1: fallback 'OUTRA' para evitar agrupamento errôneo como Caldeiraria (1.x)
        discipline: (snapRec as any).discipline || 'OUTRA',
        componentName: snapRec.componentName || 'Componente',
        firstLine: snapRec.title || 'Recomendação Removida',
        zr: snapRec.zr || '—',
        origin: 'Minuta Anterior'
      });
    }
  }

  const pendingCount = newCount + modifiedCount + removedCount;

  return {
    hasChanges: pendingCount > 0,
    totalCurrent: checklistRecs.length,
    totalSnapshot: snapshotRecs.length,
    newCount,
    modifiedCount,
    removedCount,
    pendingCount,
    items: diffItems
  };
}

/**
 * NÚMERO DA ZR do cabeçalho fixo (Melhoria 012, versão definitiva).
 *
 * O ZR sempre foi gravado como atributo de CADA Recomendação
 * (`Recommendation.zr`) — nunca uma entidade própria da Minuta — e os
 * blocos/testes já aprovados (`zr-numbering.ts`,
 * `minuta-validation-zr-rule.test.ts`) continuam funcionando exatamente
 * assim. O cabeçalho fixo da versão definitiva pede um único campo "NÚMERO
 * DA ZR:"; em vez de criar uma segunda fonte de verdade (um ZR "da Minuta"
 * desacoplado das Recomendações, que poderia divergir do que cada uma
 * mostra), este campo é DERIVADO: o(s) ZR número(s) realmente usados pelas
 * Recomendações da revisão atual, na ordem de disciplina/linha já definida
 * pelos grupos. Uma Recomendação sem ZR não aparece aqui (ela já é pendência
 * de numeração, sinalizada em outro lugar). Sem nenhuma ZR preenchida, o
 * campo mostra "—" — nunca um número inventado.
 */
export function headerZrNumberOf(groups: readonly MinutaGroup[]): string {
  const seen: string[] = [];
  for (const group of groups) {
    for (const rec of group.recommendations) {
      const zr = (rec.zr || "").trim();
      if (zr && !seen.includes(zr)) seen.push(zr);
    }
  }
  return seen.length > 0 ? seen.join(", ") : "—";
}

export function buildMinutaDocumentModel(
  contextData: {
    clientName: string;
    projectName: string;
    unitArea: string;
    osNumber: string;
    tag: string;
    equipmentType: string;
    projectCode?: string;
    fabricationCode?: string;
    fabricationStandard: string;
    materialSpec?: string;
    systemName?: string;
    inspectorName: string;
    trackingNumber?: string;
    revisionNumber?: string;
    printDate?: string;
    tenantLogo?: string | null;
    assessment?: MinutaAssessmentInfo;
  },
  recommendations: any[],
  previousSnapshot?: any
): MinutaDocumentModel {

  const groupsMap = new Map<string, MinutaRecommendation[]>();
  
  // P0-02 V5.2: Regra de ordenação oficial.
  // 1=Caldeiraria, 2=Pintura, 3=Isolamento Térmico.
  // Demais disciplinas recebem peso baseado em ordenação alfabética normalizada
  // para garantir estabilidade determinística, mas a SÉRIE NUMÉRICA final
  // é calculada dinamicamente em getFixedDisciplineSeries (4, 5, 6...).
  const getDisciplineWeight = (disc: string) => {
    const d = (disc || '').toUpperCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    if (d === 'CALDEIRARIA') return 1;
    if (d === 'PINTURA') return 2;
    if (d.includes('ISOLAMENTO')) return 3;
    // Peso alto para ordenação relativa entre extras, mas não usado como série final
    return 100 + d.charCodeAt(0);
  };

  recommendations.forEach(rec => {
    // P0-02 V5.1 Falha 2: NÃO usar 'CALDEIRARIA' como fallback padrão.
    // Se discipline estiver vazio/undefined, usar 'OUTRA' que será mapeado
    // como série >=4 por getFixedDisciplineSeries, evitando numeração 1.x errônea.
    const discipline = rec.discipline || 'OUTRA';
    if (!groupsMap.has(discipline)) {
      groupsMap.set(discipline, []);
    }
    
    // Attempt to parse subitems if the description contains bullet points or dashes
    const fullDesc = rec.description || rec.scope || '';
    const { title, body } = parseRecommendationText(fullDesc);
    const subItems: string[] = [];
    
    const lines = fullDesc.split('\n');
    lines.forEach((line: string) => {
      const trimmed = line.trim();
      if (trimmed.startsWith('-') || trimmed.startsWith('•') || /^[a-z]\)/.test(trimmed)) {
        subItems.push(trimmed);
      }
    });

    groupsMap.get(discipline)!.push({
      id: rec.id,
      recommendationId: rec.id,
      // P0-02 V5.2 Falha 1: propagar discipline explicitamente para evitar undefined no snapshot/builder.
      discipline: discipline,
      line: '', // Will be assigned preserving historical or sequentially later
      title: title,
      body: body,
      zr: rec.zr || '',
      subItems: subItems.length > 0 ? subItems : undefined,
      fullDescription: fullDesc,
      recommendationNumber: rec.recommendationNumber || rec.code || rec.recNumber,
      componentName: rec.componentName || rec.component,
      location: rec.location,
      // P0-02/P0-04: domínio usa `executeIn` ('ROUTINE'|'SHUTDOWN'); snapshot/legacy usam `executionType` ou `execution`.
      // Traduzir SHUTDOWN→PARADA / ROUTINE→ROTINA para manter consistência com o builder e a UI.
      executionType: rec.executionType || rec.execution || (rec.executeIn === 'SHUTDOWN' ? 'PARADA' : rec.executeIn === 'ROUTINE' ? 'ROTINA' : rec.executeIn),
      criticality: rec.criticality,
      scope: rec.scope,
      dimensions: rec.dimensions || rec.dimension,
      standard: rec.standard || rec.norm,
      procedure: rec.procedure,
      observation: rec.observation || rec.obs,
      notes: rec.notes
    });
  });

  // Extract discipline ordering and line numbers from previous snapshot if provided
  const prevModel = previousSnapshot?.data || previousSnapshot;
  const prevGroups: MinutaGroup[] = prevModel?.groups && Array.isArray(prevModel.groups) ? prevModel.groups : [];

  // P0-02 V4 Etapa 12: NÃO reintroduzir recomendações removidas via carry-forward.
  // Snapshot anterior serve APENAS para preservar ordenação de disciplinas e numeração de linha
  // de recomendações que AINDA existem no conjunto atual. Recomendações ausentes no conjunto
  // atual (removidas, inativadas ou movidas para outro executionType) NÃO devem reaparecer.
  // O bloco antigo que fazia push de prevRec para gruposMap foi removido intencionalmente.

  const existingDisciplineOrder: string[] = [];
  const existingLineByRecId = new Map<string, string>();
  const maxSubIndexByDiscipline = new Map<string, number>();

  prevGroups.forEach((g) => {
    if (g.discipline && !existingDisciplineOrder.includes(g.discipline)) {
      existingDisciplineOrder.push(g.discipline);
    }
    let maxSub = 0;
    (g.recommendations || []).forEach(r => {
      const rKey = r.recommendationId || r.id;
      if (rKey && r.line) {
        existingLineByRecId.set(rKey, r.line);
        const parts = r.line.split('.');
        if (parts.length === 2 && parts[1]) {
          const sub = parseInt(parts[1], 10);
          if (!isNaN(sub) && sub > maxSub) {
            maxSub = sub;
          }
        }
      }
    });
    maxSubIndexByDiscipline.set(g.discipline, maxSub);
  });

  // Determine discipline sequence:
  // 1. Existing disciplines in their original order
  // 2. New disciplines appended at the end according to getDisciplineWeight
  const allDisciplines = Array.from(groupsMap.keys());
  const newDisciplines = allDisciplines
    .filter(d => !existingDisciplineOrder.includes(d))
    .sort((a, b) => {
      const weightA = getDisciplineWeight(a);
      const weightB = getDisciplineWeight(b);
      if (weightA !== weightB) return weightA - weightB;
      return a.localeCompare(b);
    });

  const sortedDisciplines = [
    ...existingDisciplineOrder.filter(d => groupsMap.has(d)),
    ...newDisciplines
  ];

  // If no previous snapshot, sort all disciplines by canonical weight
  if (existingDisciplineOrder.length === 0) {
    sortedDisciplines.sort((a, b) => {
      const weightA = getDisciplineWeight(a);
      const weightB = getDisciplineWeight(b);
      if (weightA !== weightB) return weightA - weightB;
      return a.localeCompare(b);
    });
  }

  const groups: MinutaGroup[] = [];
  
  // P0-02 V5.2 Bloco C: Numeração fixa e determinística por disciplina.
  // CALDEIRARIA=1.x, PINTURA=2.x, ISOLAMENTO TÉRMICO=3.x.
  // Demais disciplinas recebem séries SEQUENCIAIS a partir de 4, baseadas na ordem
  // estável de sortedDisciplines (que já respeita a regra de ordenação oficial).
  const FIXED_SERIES: Record<string, number> = {};
  let nextExtraSeries = 4;
  for (const disc of sortedDisciplines) {
    const normalized = disc.toUpperCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    if (normalized === 'CALDEIRARIA') {
      FIXED_SERIES[disc] = 1;
    } else if (normalized === 'PINTURA') {
      FIXED_SERIES[disc] = 2;
    } else if (normalized.includes('ISOLAMENTO')) {
      FIXED_SERIES[disc] = 3;
    } else {
      FIXED_SERIES[disc] = nextExtraSeries++;
    }
  }

  // P0-02 V5.3: Ordenar disciplinas pela SÉRIE NUMÉRICA CRESCENTE (não alfabética).
  // Isso garante que PINTURA(2) venha antes de TUBULAÇÃO(6) mesmo que 'T' < 'P' alfabeticamente
  // ou que a ordem histórica esteja invertida.
  const orderedDisciplines = [...sortedDisciplines].sort((a, b) => {
    const seriesA = FIXED_SERIES[a] ?? 99;
    const seriesB = FIXED_SERIES[b] ?? 99;
    return seriesA - seriesB;
  });

  orderedDisciplines.forEach((discipline) => {
    const discNum = FIXED_SERIES[discipline] ?? 4;
    const recs = groupsMap.get(discipline)!;
    let currentSubIndex = maxSubIndexByDiscipline.get(discipline) || 0;

    // First assign lines: existing ones keep their historical line; new ones increment currentSubIndex
    recs.forEach((rec) => {
      const recKey = rec.recommendationId || rec.id;
      if (recKey && existingLineByRecId.has(recKey)) {
        rec.line = existingLineByRecId.get(recKey)!;
      } else {
        currentSubIndex++;
        rec.line = `${discNum}.${currentSubIndex}`;
      }
    });

    // Sort recommendations within the group by line number (ordem crescente dentro da disciplina)
    recs.sort((a, b) => {
      const partsA = a.line.split('.').map(n => parseInt(n, 10));
      const partsB = b.line.split('.').map(n => parseInt(n, 10));
      if (partsA[0] !== partsB[0]) return (partsA[0] || 0) - (partsB[0] || 0);
      return (partsA[1] || 0) - (partsB[1] || 0);
    });

    groups.push({
      discipline,
      recommendations: recs
    });
  });

  // P0-02 V4 Causa 11: executionType é obrigatório no MinutaDocumentModel.
  // Derivar do contextData (passado explicitamente pelo caller) ou das recomendações filtradas.
  const derivedExecutionType = (contextData as any).executionType
    || (recommendations.length > 0 ? (recommendations[0].executionType || recommendations[0].executeIn) : undefined);
  const normalizedExecType = (() => {
    const raw = (derivedExecutionType || '').toString().toUpperCase();
    if (raw === 'SHUTDOWN' || raw === 'PARADA') return 'PARADA';
    if (raw === 'ROUTINE' || raw === 'ROTINA') return 'ROTINA';
    return undefined;
  })();
  if (!normalizedExecType) {
    throw new Error(`P0-02 V4: executionType obrigatório em buildMinutaDocumentModel. Recebido: ${derivedExecutionType}`);
  }

  return {
    ...(contextData.assessment ? { assessment: contextData.assessment } : {}),
    clientName: contextData.clientName || 'N/A',
    projectName: contextData.projectName || 'N/A',
    unitArea: contextData.unitArea || 'N/A',
    osNumber: contextData.osNumber || 'N/A',
    tag: contextData.tag || 'N/A',
    equipmentType: contextData.equipmentType || 'N/A',
    projectCode: contextData.projectCode || 'N/A',
    fabricationCode: contextData.fabricationCode || 'N/A',
    fabricationStandard: contextData.fabricationStandard || 'N/A',
    materialSpec: contextData.materialSpec || 'N/A',
    systemName: contextData.systemName || 'N/A',
    inspectorName: contextData.inspectorName || 'N/A',
    executionType: normalizedExecType as 'PARADA' | 'ROTINA',
    trackingNumber: contextData.trackingNumber,
    revisionNumber: contextData.revisionNumber,
    printDate: contextData.printDate || new Date().toISOString(),
    tenantLogo: contextData.tenantLogo,
    groups
  };
}
